import React, { useState, useEffect } from 'react';
import { FiX, FiMapPin, FiList, FiRefreshCw, FiSave } from 'react-icons/fi';
import StatusBadge from '../StatusBadge/StatusBadge';
import styles from '../../../modules/Orders/Orders.module.css';

const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (dateValue?.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
    if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString('pt-BR');
    if (dateValue instanceof Date) return dateValue.toLocaleDateString('pt-BR');
    return '-';
};

const TrackingModal = ({ order, onClose, onUpdateStatus, addToast, statusOptions = [] }) => {
    const [selectedStatus, setSelectedStatus] = useState(order?.status || '');
    const [trackingDescription, setTrackingDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [trackingHistory, setTrackingHistory] = useState([]);

    useEffect(() => {
        if (order?.trackingHistory?.length > 0) {
            setTrackingHistory(order.trackingHistory);
        } else if (order?.createdAt) {
            setTrackingHistory([
                {
                    date: formatDate(order.createdAt),
                    status: 'Pedido criado',
                    description: 'Pedido registrado no sistema',
                    updatedBy: order.responsible || 'Sistema'
                }
            ]);
        }
    }, [order]);

    const defaultStatusOptions = [
        { value: 'pending', label: 'Pendente' },
        { value: 'approved', label: 'Aprovado' },
        { value: 'in_production', label: 'Em Produção' },
        { value: 'ready_for_picking', label: 'Pronto para Separação' },
        { value: 'shipped', label: 'Enviado' },
        { value: 'delivered', label: 'Entregue' },
        { value: 'cancelled', label: 'Cancelado' },
    ];

    const options = statusOptions.length > 0 ? statusOptions : defaultStatusOptions;

    const handleUpdateStatus = async () => {
        if (!selectedStatus || selectedStatus === order?.status) {
            onClose();
            return;
        }

        if (!trackingDescription.trim()) {
            if (addToast) addToast('Por favor, descreva a atualização do status.', 'Warning');
            return;
        }

        setLoading(true);
        try {
            await onUpdateStatus(order.id, selectedStatus, trackingDescription);
            onClose();
        } catch (error) {
            console.error('Error updating status:', error);
            if (addToast) addToast('Erro ao atualizar status. Tente novamente.', 'Error');
        } finally {
            setLoading(false);
        }
    };

    if (!order) return null;

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerMedium}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiMapPin size={24} style={{ marginRight: '10px' }} /> Rastrear Pedido</h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> - {order.clientName || order.supplierName || ''}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton} disabled={loading}>
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiMapPin size={18} /></div>
                            <h3 className={styles.sectionTitle}>Status Atual</h3>
                        </div>
                        <div className={styles.infoBox} style={{ backgroundColor: '#e8f5e9' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', width: '100%' }}>
                                <span>
                                    <strong>Situação do Pedido:</strong>{' '}
                                    <StatusBadge status={order.status} />
                                </span>
                                <span><strong>Data do Pedido:</strong> {formatDate(order.createdAt)}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiRefreshCw size={18} /></div>
                            <h3 className={styles.sectionTitle}>Atualizar Status</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div className={styles.inputGroup} style={{ marginBottom: '16px' }}>
                                <label>Novo Status *</label>
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className={styles.select}
                                    disabled={loading}
                                >
                                    {options.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles.inputGroup}>
                                <label>Descrição da Atualização *</label>
                                <textarea
                                    value={trackingDescription}
                                    onChange={(e) => setTrackingDescription(e.target.value)}
                                    className={styles.textarea}
                                    rows={3}
                                    placeholder="Descreva a atualização do status..."
                                    disabled={loading}
                                />
                                <small className={styles.fieldHint}>* Campo obrigatório</small>
                            </div>
                            {selectedStatus !== order?.status && (
                                <button
                                    type="button"
                                    onClick={handleUpdateStatus}
                                    className={styles.primaryButton}
                                    disabled={loading}
                                    style={{ marginTop: '16px', width: '100%' }}
                                >
                                    {loading ? (
                                        <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Atualizando...</>
                                    ) : (
                                        <><FiSave size={18} /> Atualizar Status</>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiList size={18} /></div>
                            <h3 className={styles.sectionTitle}>Histórico do Pedido</h3>
                        </div>
                        <div className={styles.trackingHistory}>
                            {trackingHistory.length === 0 ? (
                                <p className={styles.emptyTable}>Nenhuma atualização disponível.</p>
                            ) : (
                                trackingHistory.map((history, index) => (
                                    <div key={index} className={styles.trackingEvent}>
                                        <div className={styles.trackingDate}>{history.date}</div>
                                        <div className={styles.trackingStatus}>{history.status}</div>
                                        <div className={styles.trackingDescription}>
                                            {history.description}
                                            {history.updatedBy && (
                                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>
                                                    por: {history.updatedBy}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div className={styles.formActions}>
                    <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={loading}>
                        <FiX size={18} /> Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrackingModal;